const API_BASE_URL = "https://api-kek.sethvanwieringen.eu";

const SECTOR_LABELS = {
    "energiesysteem": "Energiesysteem",
    "gebouwde-omgeving": "Gebouwde omgeving",
    "industrie": "Industrie",
    "mobiliteit": "Mobiliteit",
    "voedsel-landbouw": "Voedsel & landbouw",
    "arbeidsmarkt-scholing": "Arbeidsmarkt & scholing",
    "circulaire-economie": "Circulaire economie",
    "groene-financiering": "Groene financiering",
};
const INTEREST_LABELS = {
    ...SECTOR_LABELS,
    "bestuur": "Bestuur",
};

function escapeHtml(value) {
    return String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

function truncateText(value, maxLength = 220) {
    const normalized = String(value ?? "").trim();
    if (normalized.length <= maxLength) {
        return normalized;
    }
    return `${normalized.slice(0, maxLength).trim().replace(/\s+\S*$/, "")}...`;
}

function renderParagraphs(value) {
    return String(value ?? "")
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean)
        .map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`)
        .join("");
}

function toggleNav() {
    const header = document.querySelector("[data-header]");
    const toggle = document.querySelector("[data-nav-toggle]");

    if (!header || !toggle) {
        return;
    }

    toggle.addEventListener("click", () => {
        const isOpen = header.classList.toggle("nav-open");
        toggle.setAttribute("aria-expanded", String(isOpen));
    });
}

async function requestJson(url, options = {}) {
    const target = url.startsWith("http://") || url.startsWith("https://")
        ? url
        : `${API_BASE_URL}${url}`;

    const response = await fetch(target, {
        ...options,
        headers: {
            Accept: "application/json",
            ...(options.headers || {}),
        },
    });

    const contentType = response.headers.get("content-type") || "";
    const body = contentType.includes("application/json")
        ? await response.json()
        : null;

    if (!response.ok) {
        const message = body && typeof body.detail === "string"
            ? body.detail
            : `Request failed: ${response.status}`;
        throw new Error(message);
    }

    return body;
}

async function fetchJson(url) {
    return requestJson(url);
}

function normalizeItems(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }
    if (payload && Array.isArray(payload.items)) {
        return payload.items;
    }
    return [];
}

const eventbriteOverviewByEndpoint = new Map();

function formatEventDateRange(item) {
    const startUtc = String(item?.start_utc || "").trim();
    if (!startUtc) {
        return "";
    }

    const start = new Date(startUtc);
    if (Number.isNaN(start.getTime())) {
        return "";
    }

    const timeZone = String(item?.timezone || "Europe/Amsterdam").trim() || "Europe/Amsterdam";
    const dateFormatter = new Intl.DateTimeFormat("nl-NL", {
        timeZone,
        day: "numeric",
        month: "short",
        year: "numeric",
    });
    const timeFormatter = new Intl.DateTimeFormat("nl-NL", {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
    });

    let label = `${dateFormatter.format(start)} · ${timeFormatter.format(start)}`;
    const endUtc = String(item?.end_utc || "").trim();
    if (endUtc) {
        const end = new Date(endUtc);
        if (!Number.isNaN(end.getTime())) {
            label += ` - ${timeFormatter.format(end)}`;
        }
    }

    return label;
}

function splitEventLocation(item) {
    const location = String(item?.location || "").trim();
    if (!location) {
        return { detail: "", venue: "" };
    }

    const parts = location
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);

    if (parts.length >= 2) {
        return {
            venue: parts[0],
            detail: parts.slice(1).join(", "),
        };
    }

    return { detail: location, venue: "" };
}

function renderEventbriteCard(item) {
    const title = String(item?.title || "").trim();
    const summary = truncateText(String(item?.summary || "").trim(), 180);
    const { detail: locationDetail, venue } = splitEventLocation(item);
    const href = String(item?.url || "").trim();
    const imageUrl = String(item?.image_url || "").trim();
    const dateLabel = formatEventDateRange(item);
    const openTag = href
        ? `<a class="feed-card__link" href="${escapeHtml(href)}" target="_blank" rel="noopener">`
        : '<div class="feed-card__link">';
    const closeTag = href ? "</a>" : "</div>";

    return `
        <article class="feed-card feed-card--event">
            ${openTag}
                ${imageUrl
                    ? `<img class="feed-card__image" src="${escapeHtml(imageUrl)}" alt="${escapeHtml(title)}" loading="lazy" />`
                    : '<div class="feed-card__image feed-card__image--placeholder"></div>'}
                <div class="feed-card__body">
                    <div class="feed-card__meta">
                        ${dateLabel ? `<time datetime="${escapeHtml(String(item?.start_utc || ""))}">${escapeHtml(dateLabel)}</time>` : ""}
                    </div>
                    <h3>${escapeHtml(title)}</h3>
                    ${locationDetail || venue ? `
                        <div class="feed-card__detail-wrap">
                            ${venue ? `<p class="feed-card__detail-feed">${escapeHtml(venue)}</p>` : ""}
                            ${locationDetail ? `<p class="feed-card__detail">${escapeHtml(locationDetail)}</p>` : ""}
                        </div>
                    ` : ""}
                    ${summary ? `<p>${escapeHtml(summary)}</p>` : ""}
                    ${href ? `
                        <span class="feed-card__cta">
                            Bekijk op Eventbrite
                            <svg class="feed-card__cta-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                                <path d="M9.5 2H14v4.5h-1.5V4.56L7.03 10.03l-1.06-1.06L11.44 3.5H9.5V2Z"></path>
                                <path d="M3 4.5h4V6H4.5v7h7V9h1.5v4a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 13V4.5Z"></path>
                            </svg>
                        </span>
                    ` : ""}
                </div>
            ${closeTag}
        </article>
    `;
}

function renderPartnerGhostLogos(count = 4) {
    return Array.from({ length: count }, () => `
        <div class="partner-logo" aria-hidden="true">
            <div class="partner-logo__media is-loading" data-staff-media>
                <span class="partner-logo__ghost staff-card__ghost" aria-hidden="true"></span>
            </div>
        </div>
    `).join("");
}

function renderStaffGhostCards(count = 3, layout = "grid") {
    const cardClass = layout === "carousel"
        ? "staff-card staff-card--ghost staff-card--featured"
        : "staff-card staff-card--ghost";
    const imageClass = layout === "carousel"
        ? "staff-card__image staff-card__image--featured staff-card__image--ghost"
        : "staff-card__image staff-card__image--ghost";
    return Array.from({ length: count }, () => `
        <article class="${cardClass}" aria-hidden="true">
            <div class="staff-card__media">
                <div class="${imageClass}"></div>
            </div>
            <div class="staff-card__body">
                <span class="staff-card__ghost staff-card__ghost--eyebrow"></span>
                <span class="staff-card__ghost staff-card__ghost--title"></span>
                <span class="staff-card__ghost staff-card__ghost--meta"></span>
                <span class="staff-card__ghost staff-card__ghost--line"></span>
                <span class="staff-card__ghost staff-card__ghost--line staff-card__ghost--line-short"></span>
                <div class="staff-card__links">
                    <span class="staff-card__ghost staff-card__ghost--link"></span>
                    <span class="staff-card__ghost staff-card__ghost--link"></span>
                </div>
            </div>
        </article>
    `).join("");
}

function renderStaffEmptyState(message) {
    return `
        <div class="staff-empty">
            <p>${escapeHtml(message)}</p>
        </div>
    `;
}

function renderStaffImage(item, className = "staff-card__image") {
    return item.photo
        ? `
            <div class="staff-card__media is-loading" data-staff-media>
                <div class="${className} staff-card__image--ghost" aria-hidden="true"></div>
                <img class="${className}" src="${escapeHtml(item.photo)}" alt="${escapeHtml(item.name)}" loading="lazy" data-staff-image />
            </div>
        `
        : `<div class="staff-card__media"><div class="${className} staff-card__image--placeholder"></div></div>`;
}

function renderStaffLinks(item) {
    const linkedIn = item.linkedin_url
        ? `<a class="staff-card__link" href="${escapeHtml(item.linkedin_url)}" target="_blank" rel="noopener">LinkedIn</a>`
        : "";
    const email = item.email_public
        ? `<a class="staff-card__link" href="mailto:${escapeHtml(item.email_public)}">E-mail</a>`
        : "";
    return `${linkedIn}${email}`;
}

function renderStaffCard(item) {
    const role = item.title || item.board_role || item.role || "";
    const companyLine = [item.job_title, item.company_name].filter(Boolean).join(", ");
    const bio = String(item.bio_short || "").trim();
    const links = renderStaffLinks(item);
    const hasExpandableBio = bio.length > 220;
    const copy = bio ? renderParagraphs(bio) : "";
    const toggle = hasExpandableBio
        ? `
            <button class="staff-card__expand" type="button" data-staff-card-toggle aria-expanded="false" aria-label="Toon volledige tekst">
                <span class="staff-card__expand-icon" aria-hidden="true"></span>
            </button>
        `
        : "";
    const footer = links || toggle
        ? `
            <div class="staff-card__footer">
                <div class="staff-card__links">${links}</div>
                ${toggle}
            </div>
        `
        : "";

    return `
        <article class="staff-card">
            ${renderStaffImage(item)}
            <div class="staff-card__body">
                ${role ? `<p class="section-label">${escapeHtml(role)}</p>` : ""}
                <h3>${escapeHtml(item.name)}</h3>
                ${companyLine ? `<p class="staff-card__meta">${escapeHtml(companyLine)}</p>` : ""}
                ${copy ? `<div class="staff-card__copy${hasExpandableBio ? " staff-card__copy--clamped" : ""}">${copy}</div>` : ""}
                ${footer}
            </div>
        </article>
    `;
}

function renderFeaturedStaffCard(item) {
    const role = item.title || item.board_role || item.role || "";
    const companyLine = [item.job_title, item.company_name].filter(Boolean).join(", ");
    const bio = truncateText(String(item.bio_short || item.bio_long || "").trim(), 500);

    return `
        <article class="staff-card staff-card--featured">
            ${renderStaffImage(item, "staff-card__image staff-card__image--featured")}
            <div class="staff-card__body">
                ${role ? `<p class="section-label">${escapeHtml(role)}</p>` : ""}
                <h3>${escapeHtml(item.name)}</h3>
                ${companyLine ? `<p class="staff-card__meta">${escapeHtml(companyLine)}</p>` : ""}
                ${bio ? `<div class="staff-card__copy"><p>${escapeHtml(bio)}</p></div>` : ""}
                <div class="staff-card__links">${renderStaffLinks(item)}</div>
            </div>
        </article>
    `;
}

function renderStaffLoadingState(count, layout) {
    const normalizedCount = Number.isNaN(count) ? 3 : count;
    if (layout === "carousel") {
        return `
            <div class="staff-carousel-shell">
                <button class="staff-carousel__button" type="button" aria-label="Vorige ambassadeur" disabled>&larr;</button>
                <div class="staff-carousel">
                    <div class="staff-carousel__track">
                        ${Array.from({ length: Math.max(1, normalizedCount) }, () => `
                            <div class="staff-carousel__slide">
                                ${renderStaffGhostCards(1, "carousel")}
                            </div>
                        `).join("")}
                    </div>
                </div>
                <button class="staff-carousel__button" type="button" aria-label="Volgende ambassadeur" disabled>&rarr;</button>
                <div class="staff-carousel__nav" aria-hidden="true"></div>
            </div>
        `;
    }
    return renderStaffGhostCards(normalizedCount);
}

function renderPartnerLogo(item, eager = false) {
    const loading = eager ? "eager" : "lazy";
    const fetchPriority = eager ? "high" : "auto";
    const inner = `
        <div class="partner-logo__media is-loading" data-staff-media>
            <span class="partner-logo__ghost staff-card__ghost" aria-hidden="true"></span>
            <img class="partner-logo__image" src="${escapeHtml(item.logo)}" alt="${escapeHtml(item.name)}" loading="${loading}" fetchpriority="${fetchPriority}" decoding="async" data-staff-image />
        </div>
    `;

    if (item.website) {
        return `
            <article class="partner-logo">
                <a class="partner-logo__link" href="${escapeHtml(item.website)}" target="_blank" rel="noopener" aria-label="${escapeHtml(item.name)}">
                    ${inner}
                </a>
            </article>
        `;
    }

    return `
        <article class="partner-logo" aria-label="${escapeHtml(item.name)}">
            ${inner}
        </article>
    `;
}

function renderPartnerLoadingState(count, layout) {
    const normalizedCount = Number.isNaN(count) ? 4 : count;
    if (layout === "carousel") {
        return `
            <div class="staff-carousel-shell">
                <button class="staff-carousel__button" type="button" aria-label="Vorige partner" disabled>&larr;</button>
                <div class="staff-carousel">
                    <div class="staff-carousel__track">
                        ${Array.from({ length: Math.max(1, normalizedCount) }, () => `
                            <div class="staff-carousel__slide">
                                ${renderPartnerGhostLogos(1)}
                            </div>
                        `).join("")}
                    </div>
                </div>
                <button class="staff-carousel__button" type="button" aria-label="Volgende partner" disabled>&rarr;</button>
                <div class="staff-carousel__nav" aria-hidden="true"></div>
            </div>
        `;
    }
    return renderPartnerGhostLogos(normalizedCount);
}

function renderPartnerCarousel(items, autoplayMs) {
    return `
        <div class="staff-carousel-shell" data-staff-carousel-shell data-autoplay-ms="${autoplayMs}">
            <button class="staff-carousel__button" type="button" data-staff-carousel-prev aria-label="Vorige partner">
                <span aria-hidden="true">&larr;</span>
            </button>
            <div class="staff-carousel" data-staff-carousel>
                <div class="staff-carousel__track" data-staff-carousel-track>
                    ${items.map((item, index) => `
                        <div class="staff-carousel__slide" data-staff-carousel-slide ${index === 0 ? 'aria-current="true"' : ""}>
                            ${renderPartnerLogo(item, true)}
                        </div>
                    `).join("")}
                </div>
            </div>
            <button class="staff-carousel__button" type="button" data-staff-carousel-next aria-label="Volgende partner">
                <span aria-hidden="true">&rarr;</span>
            </button>
            <div class="staff-carousel__nav" data-staff-carousel-nav>
                ${items.map((item, index) => `
                    <button
                        class="staff-carousel__dot${index === 0 ? " is-active" : ""}"
                        type="button"
                        data-staff-carousel-dot
                        data-index="${index}"
                        aria-label="Ga naar ${escapeHtml(item.name)}"
                        aria-pressed="${index === 0 ? "true" : "false"}"
                    ></button>
                `).join("")}
            </div>
        </div>
    `;
}

function prefetchPartnerImages(items) {
    items.forEach((item) => {
        if (!item?.logo) {
            return;
        }

        const image = new Image();
        image.decoding = "async";
        image.src = item.logo;
    });
}

function renderStaffCarousel(items, autoplayMs) {
    return `
        <div class="staff-carousel-shell" data-staff-carousel-shell data-autoplay-ms="${autoplayMs}">
            <button class="staff-carousel__button" type="button" data-staff-carousel-prev aria-label="Vorige ambassadeur">
                <span aria-hidden="true">&larr;</span>
            </button>
            <div class="staff-carousel" data-staff-carousel>
                <div class="staff-carousel__track" data-staff-carousel-track>
                    ${items.map((item, index) => `
                        <div class="staff-carousel__slide" data-staff-carousel-slide ${index === 0 ? 'aria-current="true"' : ""}>
                            ${renderFeaturedStaffCard(item)}
                        </div>
                    `).join("")}
                </div>
            </div>
            <button class="staff-carousel__button" type="button" data-staff-carousel-next aria-label="Volgende ambassadeur">
                <span aria-hidden="true">&rarr;</span>
            </button>
            <div class="staff-carousel__nav" data-staff-carousel-nav>
                ${items.map((item, index) => `
                    <button
                        class="staff-carousel__dot${index === 0 ? " is-active" : ""}"
                        type="button"
                        data-staff-carousel-dot
                        data-index="${index}"
                        aria-label="Ga naar ${escapeHtml(item.name)}"
                        aria-pressed="${index === 0 ? "true" : "false"}"
                    ></button>
                `).join("")}
            </div>
        </div>
    `;
}

function setupStaffImages(scope) {
    const mediaItems = [...scope.querySelectorAll("[data-staff-media]")];

    mediaItems.forEach((media) => {
        const image = media.querySelector("[data-staff-image]");
        if (!image) {
            return;
        }

        const markLoaded = () => {
            media.classList.remove("is-loading");
            media.classList.add("is-loaded");
        };
        const markFailed = () => {
            media.classList.remove("is-loading");
            media.classList.add("has-error");
        };

        if (image.complete && image.naturalWidth > 0) {
            markLoaded();
            return;
        }

        image.addEventListener("load", markLoaded, { once: true });
        image.addEventListener("error", markFailed, { once: true });
    });
}

function setupStaffCarousel(grid) {
    const shell = grid.querySelector("[data-staff-carousel-shell]");
    const carousel = grid.querySelector("[data-staff-carousel]");
    const track = grid.querySelector("[data-staff-carousel-track]");
    const slides = [...grid.querySelectorAll("[data-staff-carousel-slide]")];
    const dots = [...grid.querySelectorAll("[data-staff-carousel-dot]")];
    const prevButton = grid.querySelector("[data-staff-carousel-prev]");
    const nextButton = grid.querySelector("[data-staff-carousel-next]");

    if (!shell || !carousel || !track || slides.length <= 1) {
        return;
    }

    let activeIndex = 0;
    let autoplayHandle = 0;
    const autoplayMs = Number.parseInt(shell.dataset.autoplayMs || "5500", 10);
    const allowAutoplay = !window.matchMedia("(prefers-reduced-motion: reduce)").matches
        && !Number.isNaN(autoplayMs)
        && autoplayMs > 0;

    const update = (nextIndex, smooth = true) => {
        activeIndex = (nextIndex + slides.length) % slides.length;
        track.style.transitionDuration = smooth ? "" : "0s";
        track.style.transform = `translateX(-${activeIndex * 100}%)`;
        slides.forEach((slide, index) => {
            if (index === activeIndex) {
                slide.setAttribute("aria-current", "true");
            } else {
                slide.removeAttribute("aria-current");
            }
        });
        dots.forEach((dot, index) => {
            const isActive = index === activeIndex;
            dot.classList.toggle("is-active", isActive);
            dot.setAttribute("aria-pressed", String(isActive));
        });
    };

    const stopAutoplay = () => {
        if (autoplayHandle) {
            window.clearInterval(autoplayHandle);
            autoplayHandle = 0;
        }
    };

    const startAutoplay = () => {
        if (!allowAutoplay || autoplayHandle) {
            return;
        }
        autoplayHandle = window.setInterval(() => {
            update(activeIndex + 1);
        }, autoplayMs);
    };

    prevButton?.addEventListener("click", () => {
        update(activeIndex - 1);
        stopAutoplay();
        startAutoplay();
    });
    nextButton?.addEventListener("click", () => {
        update(activeIndex + 1);
        stopAutoplay();
        startAutoplay();
    });

    dots.forEach((dot) => {
        dot.addEventListener("click", () => {
            update(Number.parseInt(dot.dataset.index || "0", 10));
            stopAutoplay();
            startAutoplay();
        });
    });

    shell.addEventListener("mouseenter", stopAutoplay);
    shell.addEventListener("mouseleave", startAutoplay);
    shell.addEventListener("focusin", stopAutoplay);
    shell.addEventListener("focusout", (event) => {
        if (!shell.contains(event.relatedTarget)) {
            startAutoplay();
        }
    });

    let startX = 0;
    let deltaX = 0;
    carousel.addEventListener("pointerdown", (event) => {
        startX = event.clientX;
        deltaX = 0;
        stopAutoplay();
    });
    carousel.addEventListener("pointermove", (event) => {
        deltaX = event.clientX - startX;
    });
    carousel.addEventListener("pointerup", () => {
        if (Math.abs(deltaX) > 40) {
            update(activeIndex + (deltaX < 0 ? 1 : -1));
        }
        startAutoplay();
    });
    carousel.addEventListener("pointercancel", startAutoplay);

    update(0, false);
    startAutoplay();
}

function setupStaffCardToggles(grid) {
    const toggles = [...grid.querySelectorAll("[data-staff-card-toggle]")];

    toggles.forEach((toggle) => {
        toggle.addEventListener("click", () => {
            const card = toggle.closest(".staff-card");
            const isExpanded = card?.classList.toggle("is-expanded");

            toggle.setAttribute("aria-expanded", String(Boolean(isExpanded)));
            toggle.setAttribute("aria-label", isExpanded ? "Toon ingekorte tekst" : "Toon volledige tekst");
        });
    });
}

async function hydrateStaff() {
    const grids = [...document.querySelectorAll("[data-staff-grid]")];

    await Promise.all(grids.map(async (grid) => {
        const endpoint = grid.dataset.endpoint;
        const sector = grid.dataset.sector;
        const layout = grid.dataset.layout || "grid";
        const autoplayMs = Number.parseInt(grid.dataset.autoplayMs || "5500", 10);
        const loadingCount = Number.parseInt(grid.dataset.loadingCount || "3", 10);
        const emptyMessage = grid.dataset.emptyMessage || "Ze zijn er wel, maar we zien ze nu even niet. Probeer het straks nog eens.";
        const params = new URLSearchParams();

        if (sector) {
            params.set("sector", sector);
        }

        grid.innerHTML = renderStaffLoadingState(loadingCount, layout);

        try {
            const url = params.size ? `${endpoint}?${params.toString()}` : endpoint;
            const payload = await fetchJson(url);
            const items = normalizeItems(payload);

            if (!items.length) {
                grid.innerHTML = renderStaffEmptyState(emptyMessage);
                return;
            }

            if (layout === "carousel") {
                grid.innerHTML = renderStaffCarousel(items, Number.isNaN(autoplayMs) ? 5500 : autoplayMs);
                setupStaffImages(grid);
                setupStaffCarousel(grid);
                return;
            }

            grid.innerHTML = items.map(renderStaffCard).join("");
            setupStaffImages(grid);
            setupStaffCardToggles(grid);
        } catch (error) {
            grid.innerHTML = renderStaffEmptyState(emptyMessage);
            console.warn("Failed to hydrate staff grid", error);
        }
    }));
}

async function hydratePartners() {
    const grids = [...document.querySelectorAll("[data-partner-grid]")];

    await Promise.all(grids.map(async (grid) => {
        const endpoint = grid.dataset.endpoint;
        const layout = grid.dataset.layout || "carousel";
        const autoplayMs = Number.parseInt(grid.dataset.autoplayMs || "3200", 10);
        const loadingCount = Number.parseInt(grid.dataset.loadingCount || "4", 10);
        const emptyMessage = grid.dataset.emptyMessage || "De partnerlogo's laden nu even niet. Probeer het straks nog eens.";

        grid.innerHTML = renderPartnerLoadingState(loadingCount, layout);

        try {
            const payload = await fetchJson(endpoint);
            const items = normalizeItems(payload);

            if (!items.length) {
                grid.innerHTML = renderStaffEmptyState(emptyMessage);
                return;
            }

            if (layout === "carousel") {
                prefetchPartnerImages(items);
                grid.innerHTML = renderPartnerCarousel(items, Number.isNaN(autoplayMs) ? 3200 : autoplayMs);
                setupStaffImages(grid);
                setupStaffCarousel(grid);
                return;
            }

            grid.innerHTML = items.map(renderPartnerLogo).join("");
            setupStaffImages(grid);
        } catch (error) {
            grid.innerHTML = renderStaffEmptyState(emptyMessage);
            console.warn("Failed to hydrate partners", error);
        }
    }));
}

function renderContactCard(item) {
    const email = item.email ? `<a href="mailto:${item.email}">${item.email}</a>` : "";
    return `
        <article class="contact-card">
            <h3>${item.label}</h3>
            ${item.description ? `<p>${item.description}</p>` : ""}
            ${email ? `<p>${email}</p>` : ""}
        </article>
    `;
}

async function hydrateContactRoles() {
    const containers = [...document.querySelectorAll("[data-contact-roles]")];

    await Promise.all(containers.map(async (container) => {
        const endpoint = container.dataset.endpoint;

        try {
            const payload = await fetchJson(endpoint);
            const items = normalizeItems(payload);

            if (!items.length) {
                return;
            }

            container.innerHTML = items.map(renderContactCard).join("");
        } catch (error) {
            console.warn("Failed to hydrate contact roles", error);
        }
    }));
}

async function fetchEventbriteOverview(endpoint) {
    if (!endpoint) {
        return { planned: [], past: [], eventbrite_url: "" };
    }

    if (!eventbriteOverviewByEndpoint.has(endpoint)) {
        eventbriteOverviewByEndpoint.set(
            endpoint,
            fetchJson(endpoint).catch((error) => {
                eventbriteOverviewByEndpoint.delete(endpoint);
                throw error;
            }),
        );
    }

    return eventbriteOverviewByEndpoint.get(endpoint);
}

function updateEventbriteButton(section, url) {
    const button = section?.querySelector("[data-eventbrite-button]");
    if (!button) {
        return;
    }

    if (url) {
        button.href = url;
        button.hidden = false;
        return;
    }

    button.hidden = true;
}

async function hydrateEventbriteEvents() {
    const grids = [...document.querySelectorAll("[data-eventbrite-grid]")];

    await Promise.all(grids.map(async (grid) => {
        const endpoint = grid.dataset.endpoint;
        const category = grid.dataset.category === "past" ? "past" : "planned";
        const emptyMessage = grid.dataset.emptyMessage || "Er zijn nu geen events om te tonen.";
        const section = grid.closest("[data-eventbrite-section]");

        try {
            const payload = await fetchEventbriteOverview(endpoint);
            const items = Array.isArray(payload?.[category]) ? payload[category] : [];
            updateEventbriteButton(section, String(payload?.eventbrite_url || "").trim());

            if (!items.length) {
                grid.innerHTML = renderStaffEmptyState(emptyMessage);
                return;
            }

            grid.innerHTML = items.map(renderEventbriteCard).join("");
        } catch (error) {
            updateEventbriteButton(section, "");
            grid.innerHTML = renderStaffEmptyState(emptyMessage);
            console.warn("Failed to hydrate Eventbrite events", error);
        }
    }));
}

function setFormStatus(statusNode, message, state) {
    if (!statusNode) {
        return;
    }

    statusNode.textContent = message;
    if (state) {
        statusNode.dataset.state = state;
        return;
    }
    delete statusNode.dataset.state;
}

function getInterestPrefill(form) {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("sector");
    const defaultInterest = form.dataset.defaultInterest || "";
    return fromQuery && INTEREST_LABELS[fromQuery] ? fromQuery : defaultInterest;
}

function applyInterestPrefill(form) {
    const interest = getInterestPrefill(form);
    if (!interest) {
        return;
    }

    const target = form.querySelector(`input[name="interests"][value="${interest}"]`);
    if (target) {
        target.checked = true;
    }
}

function resetAmbassadorForm(form) {
    form.reset();
    applyInterestPrefill(form);
}

const organizationOptionsByEndpoint = new Map();

async function fetchOrganizationOptions(endpoint) {
    if (!endpoint) {
        return [];
    }

    if (!organizationOptionsByEndpoint.has(endpoint)) {
        organizationOptionsByEndpoint.set(
            endpoint,
            requestJson(endpoint)
                .then((payload) => normalizeItems(payload)
                    .map((item) => ({
                        id: String(item?.id || "").trim(),
                        name: String(item?.name || "").trim(),
                    }))
                    .filter((item) => item.id && item.name))
                .catch((error) => {
                    organizationOptionsByEndpoint.delete(endpoint);
                    throw error;
                }),
        );
    }

    return organizationOptionsByEndpoint.get(endpoint);
}

function findOrganizationMatch(options, value) {
    const normalizedValue = String(value || "").trim().toLowerCase();
    if (!normalizedValue) {
        return null;
    }
    return options.find((item) => item.name.toLowerCase() === normalizedValue) || null;
}

function normalizeOrganizationQuery(value) {
    return String(value || "").trim().toLowerCase();
}

function filterOrganizationOptions(options, value, limit = 8) {
    const normalizedQuery = normalizeOrganizationQuery(value);
    if (!normalizedQuery) {
        return options.slice(0, limit);
    }

    const startsWith = [];
    const contains = [];

    options.forEach((item) => {
        const normalizedName = normalizeOrganizationQuery(item.name);
        if (!normalizedName.includes(normalizedQuery)) {
            return;
        }

        if (normalizedName.startsWith(normalizedQuery)) {
            startsWith.push(item);
            return;
        }

        contains.push(item);
    });

    return [...startsWith, ...contains].slice(0, limit);
}

function setupOrganizationAutocomplete(form, input, hiddenInput, list, options) {
    const wrapper = input.closest("[data-organization-autocomplete]");
    if (!wrapper || !options.length) {
        return;
    }

    const baseListId = list.id || "organization-options";
    const optionIdPrefix = `${baseListId}-${Math.random().toString(36).slice(2, 8)}`;
    let filteredOptions = [];
    let activeIndex = -1;
    let blurTimeout = 0;

    const syncSelection = () => {
        const match = findOrganizationMatch(options, input.value);
        hiddenInput.value = match ? match.id : "";
        if (match) {
            input.value = match.name;
        }
        return match;
    };

    const closeSuggestions = () => {
        window.clearTimeout(blurTimeout);
        filteredOptions = [];
        activeIndex = -1;
        list.hidden = true;
        list.innerHTML = "";
        input.setAttribute("aria-expanded", "false");
        input.removeAttribute("aria-activedescendant");
    };

    const setActiveOption = (nextIndex) => {
        activeIndex = nextIndex;
        const items = [...list.querySelectorAll("[data-organization-option]")];

        items.forEach((item, index) => {
            const isActive = index === activeIndex;
            item.classList.toggle("is-active", isActive);
            item.setAttribute("aria-selected", String(isActive));
            if (isActive) {
                input.setAttribute("aria-activedescendant", item.id);
                item.scrollIntoView({ block: "nearest" });
            }
        });

        if (activeIndex < 0) {
            input.removeAttribute("aria-activedescendant");
        }
    };

    const renderSuggestions = (query = input.value) => {
        filteredOptions = filterOrganizationOptions(options, query);
        if (!filteredOptions.length) {
            closeSuggestions();
            return;
        }

        list.innerHTML = filteredOptions.map((item, index) => `
            <div
                class="ambassador-form__suggestion"
                id="${optionIdPrefix}-${index}"
                data-organization-option
                data-index="${index}"
                role="option"
                aria-selected="false"
            >${escapeHtml(item.name)}</div>
        `).join("");
        list.hidden = false;
        input.setAttribute("aria-expanded", "true");
        setActiveOption(-1);
    };

    const selectOption = (item) => {
        input.value = item.name;
        hiddenInput.value = item.id;
        closeSuggestions();
        input.dispatchEvent(new Event("change", { bubbles: true }));
    };

    input.addEventListener("focus", () => {
        renderSuggestions(input.value);
    });

    input.addEventListener("input", () => {
        syncSelection();
        renderSuggestions(input.value);
    });

    input.addEventListener("change", syncSelection);

    input.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
            closeSuggestions();
            return;
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            if (list.hidden) {
                renderSuggestions(input.value);
                return;
            }
            setActiveOption((activeIndex + 1) % filteredOptions.length);
            return;
        }

        if (event.key === "ArrowUp" && filteredOptions.length) {
            event.preventDefault();
            const nextIndex = activeIndex <= 0 ? filteredOptions.length - 1 : activeIndex - 1;
            setActiveOption(nextIndex);
            return;
        }

        if (event.key === "Enter" && activeIndex >= 0 && filteredOptions[activeIndex]) {
            event.preventDefault();
            selectOption(filteredOptions[activeIndex]);
        }
    });

    input.addEventListener("blur", () => {
        blurTimeout = window.setTimeout(() => {
            syncSelection();
            closeSuggestions();
        }, 120);
    });

    list.addEventListener("mousedown", (event) => {
        event.preventDefault();
    });

    list.addEventListener("click", (event) => {
        const option = event.target.closest("[data-organization-option]");
        if (!option) {
            return;
        }

        const nextIndex = Number.parseInt(option.dataset.index || "-1", 10);
        if (Number.isNaN(nextIndex) || !filteredOptions[nextIndex]) {
            return;
        }

        selectOption(filteredOptions[nextIndex]);
    });

    form.addEventListener("reset", () => {
        window.requestAnimationFrame(closeSuggestions);
    });

    document.addEventListener("click", (event) => {
        if (!wrapper.contains(event.target)) {
            closeSuggestions();
        }
    });
}

function showAmbassadorSuccessState(form, message) {
    const card = form.closest("[data-ambassador-card]");
    const panel = card?.querySelector("[data-ambassador-panel]");
    const successCard = card?.querySelector("[data-ambassador-success]");
    const successMessage = successCard?.querySelector("[data-form-success-message]");

    if (!card || !panel || !successCard || !successMessage) {
        return false;
    }

    successMessage.textContent = message;
    panel.hidden = false;
    successCard.hidden = false;
    card.style.setProperty("--ambassador-panel-height", `${panel.scrollHeight}px`);
    card.style.setProperty("--ambassador-success-height", `${successCard.scrollHeight}px`);

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        card.classList.add("is-success");
        panel.hidden = true;
        return true;
    }

    window.requestAnimationFrame(() => {
        card.classList.add("is-success");
    });

    const hidePanelAfterCollapse = (event) => {
        if (event.target !== panel || event.propertyName !== "max-height") {
            return;
        }

        panel.hidden = true;
        panel.removeEventListener("transitionend", hidePanelAfterCollapse);
    };

    panel.addEventListener("transitionend", hidePanelAfterCollapse);
    return true;
}

async function hydrateAmbassadorForms() {
    const forms = [...document.querySelectorAll("[data-ambassador-form]")];

    await Promise.all(forms.map(async (form) => {
        const submitButton = form.querySelector('button[type="submit"]');
        const statusNode = form.querySelector("[data-form-status]");
        const organizationInput = form.elements.organization;
        const organizationIdInput = form.elements.organization_id;
        const organizationsEndpoint = form.dataset.organizationsEndpoint || "";
        const organizationList = form.querySelector("[data-organization-list]");
        applyInterestPrefill(form);

        let organizationOptions = [];
        if (organizationInput && organizationIdInput && organizationList && organizationsEndpoint) {
            try {
                organizationOptions = await fetchOrganizationOptions(organizationsEndpoint);
                setupOrganizationAutocomplete(
                    form,
                    organizationInput,
                    organizationIdInput,
                    organizationList,
                    organizationOptions,
                );
            } catch (error) {
                console.warn("Failed to load organizations", error);
            }
        }

        form.addEventListener("submit", async (event) => {
            event.preventDefault();

            if (!form.reportValidity()) {
                return;
            }

            const interests = [...form.querySelectorAll('input[name="interests"]:checked')]
                .map((input) => String(input.value || "").trim())
                .filter(Boolean);
            const contributions = [...form.querySelectorAll('input[name="contributions"]:checked')]
                .map((input) => String(input.value || "").trim())
                .filter((value, index, values) => Boolean(value) && values.indexOf(value) === index);

            if (!interests.length) {
                setFormStatus(statusNode, "Kies minimaal een sector of bestuur.", "error");
                const firstInterest = form.querySelector('input[name="interests"]');
                if (firstInterest) {
                    firstInterest.focus();
                }
                return;
            }

            const title = String(form.elements.title.value || "").trim();
            const firstName = String(form.elements.first_name.value || "").trim();
            const middleName = String(form.elements.middle_name.value || "").trim();
            const lastName = String(form.elements.last_name.value || "").trim();
            const fullName = [firstName, middleName, lastName].filter(Boolean).join(" ");

            const payload = {
                title,
                first_name: firstName,
                middle_name: middleName,
                last_name: lastName,
                name: fullName,
                email: String(form.elements.email.value || "").trim(),
                phone: String(form.elements.phone.value || "").trim(),
                organization: String(form.elements.organization.value || "").trim(),
                organization_id: String(form.elements.organization_id.value || "").trim(),
                linkedin_username: String(form.elements.linkedin_username.value || "")
                    .trim()
                    .replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//i, "")
                    .replace(/^\/+|\/+$/g, ""),
                motivation: String(form.elements.motivation.value || "").trim(),
                interests,
                contributions,
                mailing_list: String(form.elements.mailing_list.value || "") === "yes",
                share_contact_details: true,
                contact_details_opt_out: false,
                website: String(form.elements.website.value || "").trim(),
                source_path: window.location.pathname,
                source_label: "ghost-template",
            };

            if (!payload.name) {
                setFormStatus(statusNode, "Vul minimaal je voornaam en achternaam in.", "error");
                return;
            }

            if (submitButton) {
                submitButton.disabled = true;
                submitButton.dataset.originalLabel = submitButton.textContent;
                submitButton.textContent = "Bezig met versturen...";
            }
            setFormStatus(statusNode, "", "");

            try {
                const response = await requestJson(form.dataset.endpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                });

                resetAmbassadorForm(form);
                const successMessage = response?.message || "Je aanmelding is ontvangen. We nemen contact met je op voor de volgende stap.";
                if (!showAmbassadorSuccessState(form, successMessage)) {
                    setFormStatus(statusNode, successMessage, "success");
                }
            } catch (error) {
                setFormStatus(
                    statusNode,
                    error instanceof Error
                        ? error.message
                        : "Je aanmelding kon niet worden verstuurd. Probeer het later opnieuw.",
                    "error",
                );
            } finally {
                if (submitButton) {
                    submitButton.disabled = false;
                    submitButton.textContent = submitButton.dataset.originalLabel || "Verstuur aanmelding";
                }
            }
        });
    }));
}

export default function initKekTheme() {
    toggleNav();
    hydrateStaff();
    hydratePartners();
    hydrateContactRoles();
    hydrateEventbriteEvents();
    hydrateAmbassadorForms();
}
